import { formatCurrency } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    assignModelOptions,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    ConfirmationMaterialConsumeModel,
    CONSUME_CODE,
    DateModel,
    DopModel,
    EventService,
    Fda2579FormModel,
    initialNotificationIdentifierState,
    MATERIAL_STATUS_CODE,
    MaterialModel,
    MaterialPickupComponent,
    MaterialService,
    MaterialUtilitiesService,
    ModalClass,
    NotificationIdentifier,
    RETURN_CODE,
    RETURN_REASON_CODE,
    TextModel,
    TUBE_INDICATOR,
    ValueHelperService,
} from '@evorait/pace-core';
import {
    cleanupSpqDocs,
    FormIdentifier,
    FormsConstants,
    FormsState,
    FormsStorageService,
    initialFormIdentifier,
    loadSpqDoc,
} from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import {
    faBan,
    faCheck,
    faChevronDown,
    faChevronRight,
    faExclamationTriangle,
    faPause,
    faSearch,
    faTaxi,
    faTrashAlt,
    faWrench,
} from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { catchError, mergeMap, skip, take, takeUntil, tap } from 'rxjs/operators';
import { ConfirmationMaterialConsumeUtilitiesService } from '../confirmation-material-consume-utilities.service';
import { ConfirmationMaterialGoodsInfoComponent } from '../confirmation-material-goods-info/confirmation-material-goods-info.component';
import SUBMIT_STATUS = FormsConstants.SUBMIT_STATUS;
import { ConfirmationFDA2579FormComponent } from '../../confirmation-FDA2579-form/confirmation-fda2579-form.component';

@Component({
    selector: 'pace-material-consumption-row',
    templateUrl: './material-consumption-row.component.html',
    styleUrls: ['./material-consumption-row.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialConsumptionRowComponent implements OnInit, DoCheck, OnDestroy {
    // TODO We need to change this, as the component shouldn't know anything about the model of the parent component
    @Input()
    public model: ConfirmationMaterialConsumeModel;

    @Input()
    public material: MaterialModel;

    @Input()
    public index: number;

    @Input()
    public lastElement: boolean;

    @Input()
    public parentForm: FormArray;

    @Output()
    public materialUpdatedFromPickup = new EventEmitter<MaterialModel[]>();

    @Output()
    public materialRemoved = new EventEmitter<boolean>();

    public readonly displayAWBNumber: boolean;
    public readonly displayCustomerPrice: boolean;
    public displayAmount: boolean;
    private isMaterialNonRepairable = false;
    public formInvalid = ConfigConstants.formInvalid;

    public materialConsumptionRowForm: FormGroup;
    public materialConsumptionRowFormChildren: FormArray;

    private destroy$ = new Subject<void>();

    public faChevronDown = faChevronDown;
    public faChevronRight = faChevronRight;
    public faBan = faBan;
    public faCheck = faCheck;
    public faPause = faPause;
    public faTaxi = faTaxi;
    public faExclamationTriangle = faExclamationTriangle;
    public faSearch = faSearch;
    public faWrench = faWrench;
    public faTrashAlt = faTrashAlt;
    public instDateOldLabel: string;
    public instDateNewLabel: string;
    public counterOldLabel: string;
    public counterNewLabel: string;
    public price: number;
    public currency: string;
    private formId: FormIdentifier = initialFormIdentifier;
    private notificationId: NotificationIdentifier = initialNotificationIdentifierState;

    private displayMaterialFaultCodeAndTextSubject = new BehaviorSubject<boolean>(false);
    public displayMaterialFaultCodeAndText$ = this.displayMaterialFaultCodeAndTextSubject.asObservable();

    private displayComplaintPartSubject = new BehaviorSubject<boolean>(false);
    public displayComplaintPart$ = this.displayComplaintPartSubject.asObservable();

    private displayPHIDropdownSubject = new BehaviorSubject<boolean>(false);
    public displayPHIDropdown$ = this.displayPHIDropdownSubject.asObservable();

    private displayAWBReturnFieldSubject = new BehaviorSubject<boolean>(false);
    public displayAWBReturnField$ = this.displayAWBReturnFieldSubject.asObservable();

    private displayQuestionnaireSubject = new BehaviorSubject<boolean>(false);
    public displayQuestionnaire$ = this.displayQuestionnaireSubject.asObservable();

    private displayBatteryRemovedSubject = new BehaviorSubject<boolean>(false);
    public displayBatteryRemoved$ = this.displayBatteryRemovedSubject.asObservable();

    private displayBatteryDamagedSubject = new BehaviorSubject<boolean>(false);
    public displayBatteryDamaged$ = this.displayBatteryDamagedSubject.asObservable();

    public isQuestionnaireSubmitted = false;

    private isCommercialInfoMandatoryForNonBill: boolean;
    public constructor(
        private readonly eventService: EventService,
        private readonly translate: TranslateService,
        private readonly appNotificationService: AppNotificationService,
        private readonly materialService: MaterialService,
        private readonly formBuilder: FormBuilder,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly modalService: BsModalService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly configService: ConfigService,
        private readonly confirmationMaterialConsumeUtilities: ConfirmationMaterialConsumeUtilitiesService,
        private readonly materialUtilities: MaterialUtilitiesService,
        private readonly valueHelperService: ValueHelperService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly store: Store<FormsState>,
        private readonly formStorageService: FormsStorageService,
    ) {
        this.displayAWBNumber = this.clientManager.isNgmP58();
        this.displayCustomerPrice = this.configService.getDisplayCustomerPrice();
        this.isCommercialInfoMandatoryForNonBill = this.configService.getCommercialInfMandatoryForNonBill();

        // For AA: Some of the field labels may change in future and may depend on different countries.
        // That's why we are making them configurable from gateway.
        // For more details: https://evorait.atlassian.net/browse/SHCNGMAAP2-927
        const matLabels = this.valueHelperService.getMaterialConsumptionFieldsLabels();

        const matLabelInstDateOld = matLabels.find(
            (label) => label.code === ConfigConstants.MaterialConsumedInstDateOld,
        );
        const matLabelInstDateNew = matLabels.find(
            (label) => label.code === ConfigConstants.MaterialConsumedInstDateNew,
        );
        const matLabelCounterOld = matLabels.find((label) => label.code === ConfigConstants.MaterialConsumedCounterOld);
        const matLabelCounterNew = matLabels.find((label) => label.code === ConfigConstants.MaterialConsumedCounterNew);
        this.instDateOldLabel = matLabelInstDateOld
            ? matLabelInstDateOld.descText
            : <string>this.translate.instant('CONFIRMATION_MATERIAL_INSTDATE_OLD');
        this.instDateNewLabel = matLabelInstDateNew
            ? matLabelInstDateNew.descText
            : <string>this.translate.instant('CONFIRMATION_MATERIAL_INSTDATE_NEW');
        this.counterOldLabel = matLabelCounterOld
            ? matLabelCounterOld.descText
            : <string>this.translate.instant('CONFIRMATION_MATERIAL_COUNTER_OLD');
        this.counterNewLabel = matLabelCounterNew
            ? matLabelCounterNew.descText
            : <string>this.translate.instant('CONFIRMATION_MATERIAL_COUNTER_NEW');
    }

    public ngOnInit(): void {
        const materialValidator = (): ValidationErrors | null => this.checkValidity();
        this.materialConsumptionRowFormChildren = this.formBuilder.array([]);
        this.materialConsumptionRowForm = this.formBuilder.group(
            {
                children: this.materialConsumptionRowFormChildren,
            },
            {
                validators: materialValidator,
            },
        );

        Promise.resolve(undefined)
            .then(() => {
                this.parentForm.push(this.materialConsumptionRowForm);
            })
            .catch((error: Error) =>
                this.log.error('Error in MaterialConsumptionRowComponent -> ngOnInit -> parentForm.push: ', error),
            );

        let statusOld = ConfigConstants.formValid;
        this.materialConsumptionRowForm.statusChanges
            .pipe(
                tap((statusNew: string) => {
                    if (statusOld !== statusNew) {
                        statusOld = statusNew;
                        this.changeDetectorRef.detectChanges();
                    }
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.setMaterialPriceAndCurrency();
        this.handleNonRepairableMaterials();
        this.handleComplaintsDependency();
        this.handleServicePartQuestionnaires();
        this.handleBillingDropdownDependency();
        this.triggerBillableOptionChange();
        this.displayAmount = this.material.materialSource.isOther();
        this.displayComplaintPartSubject.next(this.showComplaintPart());
        this.displayMaterialFaultCodeAndTextSubject.next(this.showMaterialFaultCodeAndText());
        this.displayPHIDropdownSubject.next(this.showPHIDropdown());
        this.displayAWBReturnFieldSubject.next(this.showAWBReturnField());
        this.displayQuestionnaireSubject.next(this.material.showServicePartQuestionnaire());
        this.displayBatteryRemovedSubject.next(this.displayBatteryRemovedQuestion());
        this.displayBatteryDamagedSubject.next(this.displayBatteryDamagedQuestion());
    }

    public ngDoCheck(): void {
        // TODO: this looks like we are calling for a change detection cycle during the change detection cycle. Needs to be corrected!
        this.changeDetectorRef.detectChanges();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    private setMaterialPriceAndCurrency(): void {
        this.price = this.material.repairPrice !== 0 ? this.material.repairPrice : this.material.customerPrice;
        this.currency = this.material.repairPrice !== 0 ? this.material.currency : this.material.customerCurrency;
    }

    public setIsMaterialNonRepairable(): void {
        this.isMaterialNonRepairable =
            this.clientManager.isNgmP58() &&
            this.model.notification.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO &&
            this.material.localScrapReturnWithoutConsumption === RETURN_CODE.CENTRAL_RETURN &&
            !this.material.isRepairable &&
            !this.material.isQualityPart &&
            !this.material.isLithiumBatteryStandAlone() &&
            !this.material.isLithiumBatteryInDevice();
    }

    private handleNonRepairableMaterials(): void {
        this.setIsMaterialNonRepairable();
        if (this.isMaterialNonRepairable && this.isMaterialReturnWithoutConsumption(this.material)) {
            this.material.setReturnTypeOptionsForNonRepairableMaterial();
        }
    }

    private handleComplaintsDependency(): void {
        if (
            this.clientManager.isNgmP58() &&
            this.model.notification.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVIVO
        ) {
            // look for on complaints option selection changed from the complaints accordion
            // set value to true or false based on any question in complaints has been selected as "yes"
            this.eventService.complaintOptionSelectedChanged$
                .pipe(
                    tap((isSelected) => {
                        this.model.isOptionYesSelectedInComplaints = isSelected ?? false;
                        this.displayComplaintPartSubject.next(this.showComplaintPart());
                    }),
                    takeUntil(this.destroy$),
                )
                .subscribe();
        }
    }

    private handleServicePartQuestionnaires(): void {
        const equipment = this.model.notification.getMainEquipment();
        if (!!equipment && this.material.spqDocId.length > 0) {
            this.formId = {
                equipmentId: equipment.equnr.value,
                formId: this.material.spqDocId,
                materialId: this.material.materialIdentifier.materialNumber.value,
            };
            this.notificationId = {
                qmNum: this.material.notificationIdentifier.qmNum,
                maNum: this.material.notificationIdentifier.maNum,
                frontId: this.material.notificationIdentifier.frontId,
            };

            // If an SPQ has been submitted then disable the 'Open' button.
            this.formStorageService
                .loadSPQSubmitStatus(this.notificationId, this.formId, this.material.materialKey)
                .pipe(
                    takeUntil(this.destroy$),
                    tap((submitStatus) => {
                        this.isQuestionnaireSubmitted = submitStatus === SUBMIT_STATUS.FINAL;
                        // Once user has submitted an SPQ, then
                        // they should not be able to change the Consumed, Return Type and Return Reasons options as well
                        this.material.consumptionCode = assignModelOptions(
                            this.material.consumptionCode,
                            {
                                ...this.material.consumptionCode.options,
                                disabled: this.isQuestionnaireSubmitted,
                            },
                            this.material,
                        );
                        this.material.returnType = assignModelOptions(
                            this.material.returnType,
                            {
                                ...this.material.returnType.options,
                                disabled: this.isQuestionnaireSubmitted,
                            },
                            this.material,
                        );
                        this.material.returnReason = assignModelOptions(
                            this.material.returnReason,
                            {
                                ...this.material.returnReason.options,
                                disabled: this.isQuestionnaireSubmitted,
                            },
                            this.material,
                        );
                        this.materialConsumptionRowFormChildren.controls.forEach((control) =>
                            control.updateValueAndValidity(),
                        );
                        this.changeDetectorRef.detectChanges();
                    }),
                    catchError(() => {
                        this.log.warn(
                            `MaterialConsumptionRowComponent: ngOnInit: could not load SPQ submit status for material: ${this.formId.materialId} and SPQ id: ${this.formId.formId}`,
                        );
                        return of(undefined);
                    }),
                )
                .subscribe();

            this.displayQuestionnaire$
                .pipe(
                    // skip the initial emission(with false) as otherwise the app will try to remove the saved spq's
                    // when returning from the forms screen to the confirmation screen. - resulting with the deletion of the
                    // spq's from the storage
                    skip(1),
                    takeUntil(this.destroy$),
                    mergeMap((displayQuestionnaire) => {
                        // If user changes their consumed/return type, and SPQ is no more displayed,
                        // then we need to delete the existing data(if any) of that spq
                        if (!displayQuestionnaire) {
                            const spqMaterial = {
                                form: this.formId,
                                notificationId: this.notificationId,
                                materialKey: this.material.materialKey,
                            };
                            this.store.dispatch(cleanupSpqDocs({ spqMaterials: [spqMaterial] }));
                        }
                        return of(undefined);
                    }),
                )
                .subscribe();
        }
    }

    public openFDA2579(): void {
        const modalInstance = this.modalService.show(ConfirmationFDA2579FormComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_FULLSCREEN,
            initialState: {
                model: this.material.fda2579FormModel,
                notification: this.material.notificationIdentifier,
            },
            ariaLabelledBy: 'internal-info-modal-title',
        });
        modalInstance.content?.modalResult
            .pipe(
                tap((savedFDAFormModel: Fda2579FormModel) => {
                    if (savedFDAFormModel) {
                        this.material.fda2579FormModel = savedFDAFormModel;
                        this.materialConsumptionRowFormChildren.controls.forEach((control) =>
                            control.updateValueAndValidity(),
                        );
                        this.changeDetectorRef.detectChanges();
                    }
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    private handleBillingDropdownDependency(): void {
        if (this.configService.getEnableBillingIndicator()) {
            if (this.material.materialSource.isOther()) {
                this.eventService.triggerMaterialBillingIndicatorsNotReceived(this.model.listMaterial.value);
            }
            if (!this.material.billableCode.options.disabled) {
                // Auto option should be removed from dropdown if it is enabled
                this.material.billableCode.value = this.material.billableCode.value.filter(
                    (billableCode) => billableCode.model.value !== ConfigConstants.BILLABLE_CODE.AUTO,
                );
            }
        }
    }

    public onConsumeChange(material: MaterialModel): void {
        if (material.isLithiumBatteryInDevice()) {
            this.resetBatteryRemovedQuestion(material);
        }
        // Return Without Consumption
        if (material.consumptionCode.selectedEntry.value === CONSUME_CODE.RETURN_WITHOUT_CONSUMPTION) {
            material.setReturnTypeOptions(CONSUME_CODE.RETURN_WITHOUT_CONSUMPTION);
            material.setMaterialTaxiAdditionalCost(true);
            if (this.isMaterialNonRepairable) {
                this.material.setReturnTypeOptionsForNonRepairableMaterial();
            }
            material.resetForReturnWithoutConsumption();
            if (
                material.localScrapReturnWithoutConsumption === RETURN_CODE.LOCAL_SCRAP ||
                material.localScrapReturnWithoutConsumption === RETURN_CODE.CENTRAL_SCRAP
            ) {
                if (material.isLithiumBatteryInDevice()) {
                    this.showSparePartLocalScrapPopup();
                } else if (material.isLithiumBatteryStandAlone()) {
                    this.showBatteryLocalScrapPopup();
                }
            }
        } else if (material.consumptionCode.selectedEntry.value === CONSUME_CODE.LEFT_ON_NOTIFICATION) {
            // Left On Notification
            material.resetForLeftOnNotification();
            material.setMaterialTaxiAdditionalCost(false);
        } else if (material.consumptionCode.selectedEntry.value === CONSUME_CODE.NOT_RECEIVED) {
            // Not Received
            material.resetForNotReceived();
            material.setMaterialTaxiAdditionalCost(false);
        } else {
            material.setReturnTypeOptions(CONSUME_CODE.CONSUMED);
            material.setMaterialTaxiAdditionalCost(true);
            if (material.isLithiumBatteryStandAlone()) {
                this.showBatteryLocalScrapPopup();
            }
            material.setBillableCode();
        }

        material.setReturnReasonOptions();
        this.eventService.triggerMaterialConsumptionOrNotReceivedChanged(this.model.listMaterial.value);
        this.triggerBillableOptionChange();
        this.displayMaterialFaultCodeAndTextSubject.next(this.showMaterialFaultCodeAndText());
        this.displayComplaintPartSubject.next(this.showComplaintPart());
        this.displayPHIDropdownSubject.next(this.showPHIDropdown());
        this.displayAWBReturnFieldSubject.next(this.showAWBReturnField());
        this.displayQuestionnaireSubject.next(this.material.showServicePartQuestionnaire());
        this.displayBatteryRemovedSubject.next(this.displayBatteryRemovedQuestion());
    }

    /**
     * On Billable dropdown option selection change
     */
    public onBillableChange(): void {
        if (this.clientManager.isNgmP58()) {
            // raise event for equipment screen commercial info desc
            this.triggerBillableOptionChange();
        }
    }

    public triggerBillableOptionChange(): void {
        const isCommercialInfoMandatory = this.isCommercialInfoMandatoryForNonBill
            ? this.isAnyNonBillableMaterialConsumed()
            : this.isAnyFocOrBillableMaterialConsumed();

        this.eventService.triggerMaterialBillableOptionChanged(isCommercialInfoMandatory);
    }

    /**
     * On Fault Code dropdown option selection change
     *
     * @param material the material model
     */
    public onFaultCodeChange(material: MaterialModel): void {
        // field is mandatory if "Fault Code" is selected, and it is not a rented material
        material.faultDescription = assignModelOptions(
            material.faultDescription,
            {
                ...material.faultDescription.options,
                required: !material.isRentalTool && material.faultCode.selectedEntries.length > 0,
            },
            material,
        );
    }

    /**
     * On check of complaint part check box
     *
     * @param material the material model
     * @param isTriggeredFromHtml true if the event is triggered from html
     */
    public onComplaintsCheckChange(material: MaterialModel, isTriggeredFromHtml = false): void {
        if (material.consumptionCode.selectedEntry) {
            const oldReturnType = material.returnType.selectedEntry?.value;
            // If the field was marked, then the “return type” has to be pre-filled with “central return” and it is not allowed to change this value
            material.setReturnTypeOptions(material.consumptionCode.selectedEntry.value);
            if (this.isMaterialNonRepairable && this.isMaterialReturnWithoutConsumption(material)) {
                this.material.setReturnTypeOptionsForNonRepairableMaterial();
            }
            material.returnType = assignModelOptions(
                material.returnType,
                {
                    ...material.returnType.options,
                    disabled: material.isComplaint.selectedEntry.value === ConfigConstants.optionYes,
                },
                material,
            );
            if (oldReturnType !== material.returnType.selectedEntry?.value) {
                this.onReturnTypeChange(material);
            }
            if (isTriggeredFromHtml) {
                this.changeDetectorRef.detectChanges();
            }
        }
    }

    /**
     * validate install date old and install date new based on date selection
     *
     * @param material the material model
     */
    public validateInstallDate(material: MaterialModel): void {
        let installationDateNew = _.cloneDeep(material.installationDateNew);
        let installationDateOld = _.cloneDeep(material.installationDateOld);

        installationDateNew = assignModelOptions(installationDateNew, {
            ...installationDateNew.options,
            maxValue: moment().startOf('days'),
        });
        installationDateOld = assignModelOptions(installationDateOld, {
            ...installationDateOld.options,
            maxValue: installationDateNew.value.clone().startOf('days'),
        });
        if (installationDateOld.value.isValid()) {
            installationDateNew = assignModelOptions(installationDateNew, {
                ...installationDateNew.options,
                minValue: installationDateOld.value.clone().startOf('days'),
            });
        }

        material.installationDateOld = <DateModel>material.set(installationDateOld);
        material.installationDateNew = <DateModel>material.set(installationDateNew);
    }

    /**
     * return the status check if the material row is consumed or returned without consumption
     *
     * @param material the material model
     * @returns true if the material is consumed or returned without consumption
     */
    public isConsumedOrReturned(material: MaterialModel): boolean {
        return this.isMaterialConsumed(material) || this.isMaterialReturnWithoutConsumption(material);
    }

    public formatCurrency(price: number): string {
        let currencyPrice = price ? price.toString() : '';
        if (this.clientManager.isNgmAAJapan()) {
            currencyPrice = formatCurrency(price, ConfigConstants.enLocale, '', '', ConfigConstants.localeNumberFormat);
        }
        return currencyPrice;
    }

    /**
     * On row item click the "collapse" value changes
     *
     * @param material the material model
     */
    public onItemClick(material: MaterialModel): void {
        if (material.materialStatus.isReceivedAndPicked) {
            // Reset consumption dropdown options only on material expand and reselect the selection if already made
            if (material.isCollapsed) {
                const selectedEntry = material.consumptionCode.selectedEntry;
                material.setConsumptionCodes();
                if (selectedEntry) {
                    material.consumptionCode.selectByPredicate((entry) => {
                        return entry.value === selectedEntry.value;
                    });
                    this.eventService.triggerMaterialConsumptionOrNotReceivedChanged(this.model.listMaterial.value);
                }
            }
            material.isCollapsed = !material.isCollapsed;
            // set material consumed to change the button style and text
            // only for first time, once you set isConsumed first time then do not update it
            if (!material.isConsumed) {
                material.isConsumed = true;
            }

            // handle special case for material return
            if (this.isAlreadyReturned(material) || this.isQuestionnaireSubmitted) {
                material.consumptionCode = assignModelOptions(
                    material.consumptionCode,
                    {
                        ...material.consumptionCode.options,
                        disabled: true,
                    },
                    material,
                );
                material.returnType = assignModelOptions(
                    material.returnType,
                    {
                        ...material.returnType.options,
                        disabled: true,
                    },
                    material,
                );
                material.returnReason = assignModelOptions(
                    material.returnReason,
                    {
                        ...material.returnReason.options,
                        disabled: true,
                    },
                    material,
                );
            }
        }
    }

    public isAlreadyReturned(material: MaterialModel): boolean {
        return material.materialStatus.statusCode === MATERIAL_STATUS_CODE.RETURNED;
    }

    /**
     * Get visibility for "Delete" Button
     *
     * @param material the material model
     * @returns true if the button should be shown
     */
    public showDeleteButton(material: MaterialModel): boolean {
        return (
            material.materialSource.isNotOrdered() &&
            (!material.materialSource.isOther() || this.clientManager.isNgmSA())
        );
    }

    public copyMaterialNumber(material: MaterialModel): void {
        material.materialNumberOld = <TextModel>(
            material.set(
                new TextModel(
                    material.materialNumberOld.name,
                    material.materialIdentifier.materialNumber.value,
                    material.materialNumberOld.options,
                ),
            )
        );
        const serialNumberDropdownModel = material.serialNumberDropdown;
        if (serialNumberDropdownModel && serialNumberDropdownModel.value.length === 1) {
            serialNumberDropdownModel.selectByValue(serialNumberDropdownModel.value[0].value);
        }
    }

    /**
     * "More Info" modal open
     *
     * @param materialModel the material model
     */
    public showMoreInfo(materialModel: MaterialModel): void {
        this.modalService.show(ConfirmationMaterialGoodsInfoComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: materialModel,
            },
            ariaLabelledBy: 'confirmation-material-goods-info-modal-title',
        });
    }

    /**
     * Add the row to parent on quantity change.
     *
     * @param index the index of the material
     */
    public deleteRow(index: number): void {
        this.materialService
            .mergeMaterial(this.model.listMaterial, index)
            .pipe(
                take(1),
                tap(() => {
                    this.eventService.triggerMaterialBillingIndicatorsNotReceived(this.model.listMaterial.value);
                    this.changeDetectorRef.detectChanges();
                    this.materialRemoved.emit(true);
                    this.triggerBillableOptionChange();
                }),
                catchError((error) => {
                    this.log.error(
                        'Error in MaterialConsumptionRowComponent -> addToRow -> materialService.mergeMaterial: ',
                        error,
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    /**
     * Split the row on quantity change
     *
     * @param materialModel the material model
     * @param index the index of the material
     */
    public splitRow(materialModel: MaterialModel, index: number): void {
        this.materialService
            .splitMaterial(this.model.listMaterial, index, materialModel.quantity.value ?? 0)
            .pipe(
                take(1),
                catchError((error: Error) => {
                    this.log.error(
                        'Error in MaterialConsumptionRowComponent -> splitRow -> materialService.splitRow: ',
                        error,
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    /**
     * Consume button click action, opens pickup missing screen if status is ordered
     * If status is picked then open consume screen
     *
     * @param material the material model
     * @param index the index of the material
     */
    public consumeMaterial(material: MaterialModel, index: number): void {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        material.materialStatus.isOrderedOrNotReceived
            ? this.openMaterialPickup(material, index)
            : this.onItemClick(material);
    }

    private openMaterialPickup(materialModel: MaterialModel, index: number): void {
        const dopModel = this.model.dopSet.find((dop: DopModel) => {
            return (
                dop.poNum.value === parseInt(materialModel.batchNumber.value, 10) ||
                dop.poNum.value === parseInt(materialModel.awbDelivery, 10)
            );
        });

        const modalInstance = this.modalService.show(MaterialPickupComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                id: this.model.notification.identifier,
                material: materialModel,
                dopModel: dopModel,
                displayNotReceived: true,
            },
            ariaLabelledBy: 'material-pickup-modal-title',
        });

        modalInstance.content?.modalResult
            .pipe(
                take(1),
                mergeMap((result) => {
                    if (result && result.materialModel) {
                        return this.materialService.setStatus(
                            this.model.listMaterial,
                            index,
                            result.pickedQuantity ?? 0,
                            result.statusCode,
                        );
                    } else {
                        return of(undefined);
                    }
                }),
                tap(() => {
                    if (
                        this.clientManager.isNgmAAANZ() ||
                        this.clientManager.isNgmAAIndia() ||
                        this.clientManager.isNgmP58OrSA()
                    ) {
                        this.eventService.triggerMaterialConsumptionOrNotReceivedChanged(this.model.listMaterial.value);
                    }
                    this.materialUpdatedFromPickup.emit(this.model.listMaterial.value);
                }),
                catchError((error) => {
                    this.log.error(
                        'Error in MaterialConsumptionRowComponent -> openMaterialPickup -> materialService.setStatus: ',
                        error,
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    private showComplaintPart(): boolean {
        const showComplaintPart = this.confirmationMaterialConsumeUtilities.showComplaintPart(
            this.material,
            this.model,
        );
        if (this.material.isComplaint.getSelectedIndex() !== ConfigConstants.confirmationComplaintsInitialSelection) {
            // reset the complaint selection if not displayed
            if (!showComplaintPart) {
                this.material.isComplaint.selectByIndex(ConfigConstants.confirmationComplaintsInitialSelection);
            }
            this.onComplaintsCheckChange(this.material);
        }
        return showComplaintPart;
    }

    public isMaterialConsumed(material: MaterialModel): boolean {
        return this.confirmationMaterialConsumeUtilities.isMaterialConsumed(material);
    }

    private showAWBReturnField(): boolean {
        return (
            this.clientManager.isNgmP58OrSA() &&
            this.isConsumedOrReturned(this.material) &&
            !this.isAlreadyReturned(this.material) &&
            this.material.returnType.selectedEntry?.value !== RETURN_CODE.LOCAL_SCRAP &&
            this.material.returnType.selectedEntry?.value !== RETURN_CODE.BOOT_STOCK
        );
    }

    // SHCNGMP58-1815: set awb return label accordingly to return type
    public getAwbReturnLabel(material: MaterialModel): string {
        if (this.materialUtilities.isSpecialDangerousGoodsReturn(material)) {
            return <string>this.translate.instant('CONFIRMATION_MATERIAL_AWBRETURN_SPECIAL');
        }
        if (this.isReturnTypeCentralReturn(material)) {
            return <string>this.translate.instant('CONFIRMATION_MATERIAL_AWBRETURN_CENTRAL');
        } else if (material.returnType.selectedEntry?.value === RETURN_CODE.CENTRAL_SCRAP) {
            return <string>this.translate.instant('CONFIRMATION_MATERIAL_AWBRETURN_CENTRAL_SCRAP');
        } else {
            return <string>this.translate.instant('CONFIRMATION_MATERIAL_AWBRETURN');
        }
    }

    /**
     * For NGMAA: If the values of NDP_PARTS-AWBNR(awbDelivery) and NDP_PARTS-CHARG(batchNumber) are same, then show the Batch Number.
     *
     * @param material the material model
     * @returns true if the batch number should be displayed
     */
    public displayBatchNumber(material: MaterialModel): boolean {
        return (
            this.clientManager.isNgmP58OrSA() ||
            (this.clientManager.isNgmAA() &&
                (material.awbDelivery === material.batchNumber.value ||
                    (this.configService.getDisplayBatchNumber() && material.materialSource.isOther())))
        );
    }

    public displayInstallationDateFields(material: MaterialModel): boolean {
        return (
            this.isMaterialConsumed(material) &&
            (material.tubeIndicator === TUBE_INDICATOR.TUBE_PART_INDICATOR ||
                (this.clientManager.isNgmP58() && material.tubeIndicator === TUBE_INDICATOR.DETECTOR_INDICATOR))
        );
    }

    public displayCounterFields(material: MaterialModel): boolean {
        return this.isMaterialConsumed(material) && material.tubeIndicator === TUBE_INDICATOR.TUBE_PART_INDICATOR;
    }

    /**
     * For NGMP58 - This dropdown will only be displayed if material is consumed (C) and if TUBE_INDICATOR = H or M
     * and if dropdown options "DATA_MEDIUM" are defined at the backend
     *
     * @returns true if the PHI dropdown should be displayed
     */
    private showPHIDropdown(): boolean {
        return (
            this.clientManager.isNgmP58() &&
            this.isMaterialConsumed(this.material) &&
            this.material.phiDataCode.value.length > 0 &&
            (this.material.tubeIndicator === TUBE_INDICATOR.HARDWARE_INDICATOR ||
                this.material.tubeIndicator === TUBE_INDICATOR.MEMORY_INDICATOR)
        );
    }

    public showFieldWithSerialPJIndicator(material: MaterialModel): boolean {
        return this.isMaterialConsumed(material) && material.isMaterialSerialized();
    }

    public showMaterialOldField(material: MaterialModel): boolean {
        return (
            !material.isRentalTool &&
            this.isMaterialConsumed(material) &&
            (this.isReturnTypeCentralReturn(material) ||
                (material.isMaterialSerialized() && material.isMaterialScrapped()))
        );
    }

    public isMaterialReturnWithoutConsumption(material: MaterialModel): boolean {
        return material.consumptionCode.selectedEntry?.value === CONSUME_CODE.RETURN_WITHOUT_CONSUMPTION;
    }

    public showReturnReasonField(material: MaterialModel): boolean {
        return (
            (this.isMaterialNonRepairable && this.isMaterialReturnWithoutConsumption(material)) ||
            ((material.isRentalTool || this.isMaterialReturnWithoutConsumption(material)) &&
                this.isReturnTypeCentralReturn(material))
        );
    }

    /**
     * This function checks for the valid AWB return number
     * AWB return need to be unique (Except for NGMAA and SA where AWB is not guaranteed to be unique)
     * If already AWB return number exists then warning popup will be shown
     *
     * @param currentMaterial the current material model
     */
    public checkAwbValidity(currentMaterial: MaterialModel): void {
        if (this.clientManager.isNgmAP0()) {
            return;
        }

        const savedMaterials = this.materialService.getMaterials();

        // check if awb return number match with the locally saved material awb return number
        let existingAwb = savedMaterials.find(
            (material) =>
                material.awbReturn.value === currentMaterial.awbReturn.value &&
                material.materialKey !== currentMaterial.materialKey,
        );

        // Also check if awb return number match with the current sets of material awb return number
        // which is not yet saved
        if (!existingAwb) {
            existingAwb = this.model.listMaterial.value.find(
                (material) =>
                    material.awbReturn.value === currentMaterial.awbReturn.value &&
                    material.materialKey !== currentMaterial.materialKey,
            );
        }

        if (existingAwb && currentMaterial.awbReturn.value) {
            const awbReturn = _.cloneDeep(currentMaterial.awbReturn);
            awbReturn.value = '';
            currentMaterial.awbReturn = <TextModel>currentMaterial.set(awbReturn);
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('CONFIRMATION_MATERIAL_AWB_RETURN_TITLE'),
                <string>this.translate.instant('CONFIRMATION_MATERIAL_AWB_RETURN_BODY'),
            );
        }
        this.changeDetectorRef.detectChanges();
    }

    /**
     * Shows Material Fault Code and Fault Text material if it is Central Return
     * 1. MK-Code = C (Consumed) OR
     * 2. MK-Code = D (Return Without Consumption) and Return Reason Code is "Dead on Arrival or Transportation Damage or Incorrect Delivery"
     * Also resets fault code and description if no longer displayed
     *
     * @returns true if the fault code and text should be displayed
     */
    private showMaterialFaultCodeAndText(): boolean {
        let showMaterialFaultCodeAndText = false;
        if (this.isReturnTypeCentralReturn(this.material)) {
            showMaterialFaultCodeAndText =
                this.isMaterialConsumed(this.material) ||
                (this.isMaterialReturnWithoutConsumption(this.material) &&
                    (this.material.returnReason.selectedEntry?.value === RETURN_REASON_CODE.DEAD_ON_ARRIVAL ||
                        this.material.returnReason.selectedEntry?.value === RETURN_REASON_CODE.INCORRECT_DELIVERY ||
                        this.material.returnReason.selectedEntry?.value === RETURN_REASON_CODE.TRANSPORTATION_DAMAGE));
        }
        // Clear Fault Code and Fault description if no longer displayed
        if (!showMaterialFaultCodeAndText) {
            this.material.resetFaultCodeAndText();
        }
        return showMaterialFaultCodeAndText;
    }

    /**
     * On Return reason dropdown option selection change
     * If a material is non-repairable and Dead on Arrival is selected in return reason, then Local scrap must be selected
     * If a material is repairable then central return must be selected irrespective of any return reason
     *
     * @param material the material model
     */
    public onReturnReasonChange(material: MaterialModel): void {
        if (this.isMaterialReturnWithoutConsumption(material) && material.returnReason.selectedEntry) {
            if (material.isLithiumBatteryStandAlone()) {
                material.setReturnTypeOptionsForLithiumBatteryStandAlone();
                if (material.returnType.selectedEntry.value === RETURN_CODE.LOCAL_SCRAP) {
                    this.onReturnTypeChange(material);
                    this.showBatteryLocalScrapPopup();
                }
            }
            if (material.isLithiumBatteryInDevice()) {
                if (!this.isReturnReasonTransportDamageOrDoA(material)) {
                    this.resetBatteryRemovedQuestion(material);
                }
                this.displayBatteryRemovedSubject.next(this.displayBatteryRemovedQuestion());
            }
            if (this.isMaterialNonRepairable) {
                material.setReturnTypeForDeadOnArrivalMaterial();
            }
        }
        this.displayMaterialFaultCodeAndTextSubject.next(this.showMaterialFaultCodeAndText());
        this.displayAWBReturnFieldSubject.next(this.showAWBReturnField());
        this.displayQuestionnaireSubject.next(this.material.showServicePartQuestionnaire());
    }

    /**
     * On Return type dropdown option selection change
     *
     * @param material the material model
     */
    public onReturnTypeChange(material: MaterialModel): void {
        material.returnReason.deselect();
        // For NGM P58, if material is to be return with central scrap then reason code should be always '380'
        // https://evorait.atlassian.net/browse/SHCNGMP58-1992
        if (material.consumptionCode.selectedEntry && material.returnType.selectedEntry)
            if (material.returnType.selectedEntry.value === RETURN_CODE.CENTRAL_SCRAP) {
                material.returnReason.selectByValue(this.configService.getDefaultReasonCodeForCentralScrap());
            } else if (
                // If material is non-repairable and return reason is selected DEAD_ON_ARRIVAL then return type should be "local scrap"
                // vice versa If material is non-repairable and local scrap is selected then return reason should be DEAD ON ARRIVAL
                this.material.returnType.selectedEntry.value === RETURN_CODE.LOCAL_SCRAP &&
                this.isMaterialNonRepairable &&
                this.isMaterialReturnWithoutConsumption(material)
            ) {
                this.material.returnReason.selectByValue(RETURN_REASON_CODE.DEAD_ON_ARRIVAL);
            } else if (this.material.isRentalTool) {
                this.material.returnReason.selectByValue(RETURN_REASON_CODE.DIAGNOSIS_SEALED);
            }
        this.displayMaterialFaultCodeAndTextSubject.next(this.showMaterialFaultCodeAndText());
        this.displayAWBReturnFieldSubject.next(this.showAWBReturnField());
        material.loadAWBReturnFieldProperties();
        this.displayQuestionnaireSubject.next(this.material.showServicePartQuestionnaire());
    }

    /**
     * Check if Billable dropdown of any material is selected as BILL or FOC
     *
     * Remarks:
     * For P5 additional FOC options were introduced (P01, P02, P03, ...). These have to return true as well.
     * Problem: NON_BILLABLE code overlaps with P53 - Customer Damage-C. Both codes are 'N'.
     *
     * @returns true if any material is selected as BILL or FOC
     */
    private isAnyFocOrBillableMaterialConsumed(): boolean {
        return this.model.listMaterial.value.some(
            (selectedMaterial) =>
                selectedMaterial.consumptionCode.selectedEntry?.value === CONSUME_CODE.CONSUMED &&
                (selectedMaterial.billableCode.selectedEntry?.value ===
                    <string>ConfigConstants.BILLABLE_CODE.BILLABLE ||
                    selectedMaterial.billableCode.selectedEntry?.value ===
                        <string>ConfigConstants.BILLABLE_CODE.COURTESY ||
                    this.valueHelperService.codeListForId[
                        ConfigConstants.valueHelperIdFocAdditionalMaterialCodes
                    ]?.includes(selectedMaterial.billableCode.selectedEntry?.value)),
        );
    }

    /**
     * Check if Billable dropdown of any material is selected as Non-Bill and material is consumed
     *
     * @returns true if any material is selected as NON-BILL
     */
    private isAnyNonBillableMaterialConsumed(): boolean {
        return this.model.listMaterial.value.some(
            (selectedMaterial) =>
                selectedMaterial.consumptionCode.selectedEntry?.value === CONSUME_CODE.CONSUMED &&
                selectedMaterial.billableCode.selectedEntry?.value ===
                    (ConfigConstants.BILLABLE_CODE.NON_BILLABLE as string),
        );
    }

    public displayInputFields(material: MaterialModel): boolean {
        const isLiBatteryInDeviceForCentralReturnWithoutConsumption =
            this.material.isLithiumBatteryInDevice() &&
            this.isMaterialReturnWithoutConsumption(this.material) &&
            this.material.localScrapReturnWithoutConsumption === RETURN_CODE.CENTRAL_RETURN;
        if (!isLiBatteryInDeviceForCentralReturnWithoutConsumption) {
            const displayBatteryRemovedQuestion = this.displayBatteryRemovedQuestion();
            const displayBatteryDamagedQuestion = this.displayBatteryDamagedQuestion();
            const isBatteryRemovedQuestionAnswered =
                displayBatteryRemovedQuestion &&
                material.isBatteryRemoved.selectedEntry.value !== ConfigConstants.optionValueNA;
            const isBatteryDamagedQuestionAnswered =
                displayBatteryDamagedQuestion &&
                material.isBatteryDamaged.selectedEntry.value !== ConfigConstants.optionValueNA;
            return (
                (!displayBatteryRemovedQuestion || isBatteryRemovedQuestionAnswered) &&
                (!displayBatteryDamagedQuestion || isBatteryDamagedQuestionAnswered)
            );
        }
        return true;
    }

    private displayBatteryDamagedQuestion(): boolean {
        return this.material.isBatteryRemoved.selectedEntry.value === ConfigConstants.optionValueNo;
    }

    private displayBatteryRemovedQuestion(): boolean {
        if (this.material.isLithiumBatteryInDevice()) {
            if (this.isMaterialConsumed(this.material)) {
                return true;
            }
            if (
                this.isMaterialReturnWithoutConsumption(this.material) &&
                this.material.localScrapReturnWithoutConsumption === RETURN_CODE.CENTRAL_RETURN
            ) {
                const isBatteryRemovedAnswered =
                    this.material.isBatteryRemoved.selectedEntry.value !== ConfigConstants.optionValueNA;
                return isBatteryRemovedAnswered || this.isReturnReasonTransportDamageOrDoA(this.material);
            }
        }
        return false;
    }

    public onBatteryDamagedChange(): void {
        if (this.isMaterialConsumed(this.material)) {
            if (this.material.isBatteryDamaged.selectedEntry.value === ConfigConstants.optionValueYes) {
                this.showSparePartLocalScrapPopup();
            } else if (this.material.isBatteryDamaged.selectedEntry.value === ConfigConstants.optionValueNo) {
                if (this.material.localScrapReturnConsumed !== RETURN_CODE.CENTRAL_RETURN) {
                    this.showBatteryLocalScrapPopup();
                }
            }
            this.material.setReturnTypeOptionsForLithiumBatteryInDevice();
            this.onReturnTypeChange(this.material);
        }

        if (this.isMaterialReturnWithoutConsumption(this.material)) {
            this.resetForLiBatteryInDeviceReturnWithoutConsumption();
        }
    }

    private resetForLiBatteryInDeviceReturnWithoutConsumption(): void {
        const selectedReturnReason = this.isReturnTypeCentralReturn(this.material)
            ? this.material.returnReason.selectedEntry?.value
            : undefined;
        const selectedFaultCode = this.material.faultCode.selectedEntry?.value;
        const faultDescription = this.material.faultDescription.value;
        this.material.setReturnTypeOptionsForLithiumBatteryInDevice();
        this.onReturnTypeChange(this.material);
        if (this.material.isBatteryDamaged.selectedEntry.value === ConfigConstants.optionValueYes) {
            this.showSparePartLocalScrapPopup();
        } else {
            this.material.returnReason.selectByPredicate((entry) => entry.value === selectedReturnReason);
            this.material.faultCode.selectByPredicate((entry) => entry.value === selectedFaultCode);
            this.material.faultDescription.value = faultDescription;
            this.displayMaterialFaultCodeAndTextSubject.next(this.showMaterialFaultCodeAndText());
        }
    }

    public onBatteryRemovedChange(): void {
        this.resetBatteryDamagedQuestion(this.material);
        this.displayBatteryDamagedSubject.next(this.displayBatteryDamagedQuestion());
        if (this.isMaterialConsumed(this.material)) {
            if (this.material.isBatteryRemoved.selectedEntry.value === ConfigConstants.optionValueYes) {
                this.showBatteryLocalScrapPopup();
                this.material.setReturnTypeOptionsForLithiumBatteryInDevice();
                this.onReturnTypeChange(this.material);
            }
        }
        if (this.isMaterialReturnWithoutConsumption(this.material)) {
            if (this.material.isBatteryRemoved.selectedEntry.value === ConfigConstants.optionValueYes) {
                this.showBatteryLocalScrapPopup();
            }
            if (
                !this.isReturnTypeCentralReturn(this.material) &&
                this.material.isBatteryRemoved.selectedEntry.value === ConfigConstants.optionValueNA
            ) {
                this.resetForLiBatteryInDeviceReturnWithoutConsumption();
            }
            this.material.loadAWBReturnFieldProperties();
        }
    }

    private resetBatteryRemovedQuestion(material: MaterialModel): void {
        if (material.isBatteryRemoved.selectedEntry.value !== ConfigConstants.optionValueNA) {
            this.resetBatteryDamagedQuestion(material);
            const isBatteryRemoved = _.cloneDeep(material.isBatteryRemoved);
            isBatteryRemoved.selectByPredicate((entry) => entry.value === ConfigConstants.optionValueNA);
            material.isBatteryRemoved = isBatteryRemoved;
            this.displayBatteryDamagedSubject.next(this.displayBatteryDamagedQuestion());
            material.loadAWBReturnFieldProperties();
        }
    }

    private resetBatteryDamagedQuestion(material: MaterialModel): void {
        const isBatteryDamaged = _.cloneDeep(material.isBatteryDamaged);
        isBatteryDamaged.selectByPredicate((entry) => entry.value === ConfigConstants.optionValueNA);
        material.isBatteryDamaged = isBatteryDamaged;
    }

    private showBatteryLocalScrapPopup(): void {
        const showLocalOrCentralScrapPopup = this.showLocalOrCentralScrapPopup();
        const title = showLocalOrCentralScrapPopup
            ? 'CONFIRMATION_MATERIAL_BATTERY_LOCAL_CENTRAL_SCRAP_TITLE'
            : 'CONFIRMATION_MATERIAL_BATTERY_LOCAL_SCRAP_TITLE';
        const body = showLocalOrCentralScrapPopup
            ? 'CONFIRMATION_MATERIAL_BATTERY_LOCAL_CENTRAL_SCRAP_BODY'
            : 'CONFIRMATION_MATERIAL_BATTERY_LOCAL_SCRAP_BODY';
        this.appNotificationService.setAppNotification(
            AppNotificationType.INFO,
            <string>this.translate.instant(title),
            <string>this.translate.instant(body),
        );
    }

    private showSparePartLocalScrapPopup(): void {
        const showLocalOrCentralScrapPopup = this.showLocalOrCentralScrapPopup();
        const title = showLocalOrCentralScrapPopup
            ? 'CONFIRMATION_MATERIAL_SPAREPART_LOCAL_CENTRAL_SCRAP_TITLE'
            : 'CONFIRMATION_MATERIAL_SPAREPART_LOCAL_SCRAP_TITLE';
        const body = showLocalOrCentralScrapPopup
            ? 'CONFIRMATION_MATERIAL_SPAREPART_LOCAL_CENTRAL_SCRAP_BODY'
            : 'CONFIRMATION_MATERIAL_SPAREPART_LOCAL_SCRAP_BODY';
        this.appNotificationService.setAppNotification(
            AppNotificationType.INFO,
            <string>this.translate.instant(title),
            <string>this.translate.instant(body),
        );
    }

    private showLocalOrCentralScrapPopup(): boolean {
        const isCentralScrapForDangerousGoodsAllowed =
            this.configService.getDisplayCentralScrap() && this.configService.getDisplayCentralScrapForDangerousGoods();
        if (this.material.isLithiumBatteryInDevice() && isCentralScrapForDangerousGoodsAllowed) {
            const isBatteryDamaged =
                this.material.isBatteryDamaged.selectedEntry?.value === ConfigConstants.optionValueYes;
            const isBatteryNotDamaged =
                this.material.isBatteryDamaged.selectedEntry?.value === ConfigConstants.optionValueNo;
            if (this.isMaterialConsumed(this.material)) {
                const returnIndicatorIsNotCentralReturn =
                    this.material.localScrapReturnConsumed !== RETURN_CODE.CENTRAL_RETURN;
                return isBatteryDamaged || (isBatteryNotDamaged && returnIndicatorIsNotCentralReturn);
            }
            if (this.isMaterialReturnWithoutConsumption(this.material)) {
                const returnIndicatorIsNotCentralReturn =
                    this.material.localScrapReturnWithoutConsumption !== RETURN_CODE.CENTRAL_RETURN;
                return isBatteryDamaged || returnIndicatorIsNotCentralReturn;
            }
        }
        return false;
    }

    private isReturnTypeCentralReturn(material: MaterialModel): boolean {
        return material.returnType.selectedEntry?.value === RETURN_CODE.CENTRAL_RETURN;
    }

    private isReturnReasonTransportDamageOrDoA(material: MaterialModel): boolean {
        return (
            material.returnReason.selectedEntry?.value === RETURN_REASON_CODE.TRANSPORTATION_DAMAGE ||
            material.returnReason.selectedEntry?.value === RETURN_REASON_CODE.DEAD_ON_ARRIVAL
        );
    }

    /**
     * Open Spare parts questionnaire form
     */
    public openQuestionnaire(): void {
        this.log.debug(
            `MaterialConsumptionRowComponent: openQuestionnaire: opening questionnaire for ${this.material.materialIdentifier.materialNumber}`,
        );
        this.store.dispatch(
            loadSpqDoc({
                form: this.formId,
                notification: this.notificationId,
                materialKey: this.material.materialKey,
                language: this.model.notification.languageId,
                materialNumber: this.material.materialIdentifier.materialNumber.value,
            }),
        );
    }

    private checkValidity(): null | ValidationErrors {
        const errors: ValidationErrors = {};

        if (this.material.showServicePartQuestionnaire() && !this.isQuestionnaireSubmitted) {
            errors.required = 'SPQ Form must be submitted';
        }

        if (this.material.isFDA2579FormInvalid()) {
            errors.required = 'FDA form is Invalid';
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public getDeclaredOrReturnButtonLabel(material: MaterialModel): string {
        if (this.isAlreadyReturned(material)) {
            return 'DASHBOARD_SIDEMENU_MATERIAL_HISTORY_STATUS_RETURNED';
        }
        return material.isConsumed ? 'CONFIRMATION_MATERIAL_LABEL_CONSUMED' : 'CONFIRMATION_MATERIAL_LABEL_CONSUME';
    }
}
